'use client';

import { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslations } from 'next-intl';
import { Box, HStack, VStack } from '@terra/ui-styled-system';
import {
  BodyDefault,
  BodyStrong,
  Button,
  Spinner,
  Progress,
  Combobox,
  IconButton,
  Input,
  MetaDefault,
  Switch,
  createSelectListCollection,
  PDF_TEMPLATE_IDS,
} from '@terra/ui-design-system';
import {
  AlertCircleIcon,
  Tick01Icon,
  ArrowUpDownIcon,
  File01Icon,
  Image01Icon,
  SquareLock01Icon,
} from '@terra/ui-icons';
import { usePdfGeneration, type PdfJob } from '../../hooks/usePdfGeneration';
import { toasterApi } from '@terra/ui-design-system';
import type { ReportsReturnDto } from '@terra/shared-contracts-reports';
import {
  countPublishedRelatedFindings,
  getPublishedRelatedFindings,
} from '../../utils/get-published-related-findings';

type ReportGenerateTabProps = {
  report: ReportsReturnDto;
  onComplete: () => void;
};

export function ReportGenerateTab({
  report,
  onComplete,
}: ReportGenerateTabProps) {
  const t = useTranslations('reportGenerateTab');
  const tCommon = useTranslations('common');
  const {
    templates,
    isLoadingTemplates,
    currentJob,
    isGenerating,
    generatePdf,
    resetJob,
    fetchTemplates,
  } = usePdfGeneration();
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>('');
  const [includeScreenshots, setIncludeScreenshots] = useState(true);
  const [includeRemediation, setIncludeRemediation] = useState(false);
  const [
    includeFindingsHighLevelOverview,
    setIncludeFindingsHighLevelOverview,
  ] = useState(false);
  const [passwordProtect, setPasswordProtect] = useState(true);
  const [customPassword, setCustomPassword] = useState('');
  const lastHandledJobEventRef = useRef<string>('');

  useEffect(() => {
    fetchTemplates();
    resetJob();
  }, [fetchTemplates, resetJob]);
  useEffect(() => {
    if (templates.length > 0 && !selectedTemplateId) {
      const defaultTemplate = templates.find((template) => template.isDefault);

      setSelectedTemplateId(defaultTemplate?.id ?? templates[0].id);
    }
  }, [templates, selectedTemplateId]);
  useEffect(() => {
    const jobEventKey = [
      currentJob?.id ?? '',
      currentJob?.status ?? '',
      currentJob?.downloadUrl ?? '',
      currentJob?.error ?? '',
    ].join(':');

    if (jobEventKey === lastHandledJobEventRef.current) {
      return;
    }

    if (currentJob?.status === 'COMPLETED' && currentJob.downloadUrl) {
      lastHandledJobEventRef.current = jobEventKey;
      setTimeout(() => {
        toasterApi.success({
          title: t('toast.pdfGenerated.title'),
          description: t('toast.pdfGenerated.description'),
        });
        onComplete();
      }, 0);
    } else if (currentJob?.status === 'FAILED') {
      lastHandledJobEventRef.current = jobEventKey;
      setTimeout(() => {
        toasterApi.error({
          title: t('toast.pdfGenerationFailed.title'),
          description:
            currentJob.error ?? t('toast.pdfGenerationFailed.description'),
        });
      }, 0);
    }
  }, [
    currentJob?.id,
    currentJob?.status,
    currentJob?.downloadUrl,
    currentJob?.error,
    onComplete,
    t,
  ]);
  const isExecutiveSummary =
    selectedTemplateId === PDF_TEMPLATE_IDS.EXECUTIVE_SUMMARY;
  const handleGenerate = useCallback(async () => {
    if (!selectedTemplateId) {
      toasterApi.error({
        title: t('toast.templateRequired.title'),
        description: t('toast.templateRequired.description'),
      });

      return;
    }

    const findingIds = getPublishedRelatedFindings({
      relatedFindings: report.relatedFindings,
    })
      .map((finding) => finding.id)
      .filter((id): id is string => Boolean(id));

    await generatePdf({
      reportId: report.id,
      templateId: selectedTemplateId,
      findingIds,
      options: {
        includeScreenshots: isExecutiveSummary ? false : includeScreenshots,
        includeRemediation: isExecutiveSummary ? false : includeRemediation,
        includeFindingsHighLevelOverview: isExecutiveSummary
          ? includeFindingsHighLevelOverview
          : false,
        passwordProtect: passwordProtect || undefined,
        password:
          passwordProtect && customPassword ? customPassword : undefined,
      },
    });
  }, [
    selectedTemplateId,
    report,
    generatePdf,
    includeScreenshots,
    includeRemediation,
    isExecutiveSummary,
    includeFindingsHighLevelOverview,
    passwordProtect,
    customPassword,
    t,
  ]);
  const templateCollection = createSelectListCollection({
    items: templates.map((template) => ({
      label: template.name,
      value: template.id,
    })),
  });
  const getProgressColor = (status: PdfJob['status'] | undefined) => {
    switch (status) {
      case 'COMPLETED':
        return 'green';
      case 'FAILED':
        return 'red';
      default:
        return 'blue';
    }
  };

  const findingsCount = countPublishedRelatedFindings({
    relatedFindings: report.relatedFindings,
  });

  return (
    <VStack gap={3} w="full" alignItems="stretch">
      {isGenerating || currentJob ? (
        <VStack gap={3} w="full" py={3}>
          <VStack gap={1.5} w="full">
            <HStack justifyContent="space-between" w="full">
              <BodyDefault>
                {currentJob?.message ?? t('processing')}
              </BodyDefault>
              <MetaDefault color="fg.muted">
                {currentJob?.progress ?? 0}%
              </MetaDefault>
            </HStack>
            <Progress
              value={currentJob?.progress ?? 0}
              max={100}
              size="sm"
              colorPalette={getProgressColor(currentJob?.status)}
            />
          </VStack>

          {currentJob?.status === 'FAILED' && (
            <HStack gap={1.5} p={2} bg="bg.subtle" borderRadius="md" w="full">
              <AlertCircleIcon size={14} />
              <MetaDefault color="fg.error">
                {currentJob.error ?? t('generationFailed')}
              </MetaDefault>
            </HStack>
          )}

          {currentJob?.status === 'COMPLETED' && (
            <Button size="sm" w="full" onClick={() => resetJob()}>
              <Tick01Icon size={14} />
              {t('doneViewInFilesTab')}
            </Button>
          )}
        </VStack>
      ) : (
        <VStack gap={3} w="full">
          <Box p={2} bg="bg.subtle" borderRadius="md" w="full">
            <MetaDefault color="fg.muted">
              {t('intro')}{' '}
              <BodyStrong as="span">
                {t('findingsCount', { count: findingsCount })}
              </BodyStrong>
            </MetaDefault>
          </Box>

          <VStack gap={1} alignItems="stretch" w="full">
            <BodyDefault fontWeight="medium">{t('template.label')}</BodyDefault>
            {isLoadingTemplates ? (
              <HStack gap={1.5} py={1}>
                <Spinner size="sm" />
                <MetaDefault color="fg.muted">
                  {t('template.loading')}
                </MetaDefault>
              </HStack>
            ) : (
              <Combobox.Root
                width="full"
                size="sm"
                collection={templateCollection}
                value={selectedTemplateId ? [selectedTemplateId] : []}
                onValueChange={(details) => {
                  const nextTemplateId = details.value[0] ?? '';
                  const nextIsExecutiveSummary =
                    nextTemplateId === PDF_TEMPLATE_IDS.EXECUTIVE_SUMMARY;

                  if (nextIsExecutiveSummary) {
                    setIncludeFindingsHighLevelOverview(false);
                  }

                  setSelectedTemplateId(nextTemplateId);
                }}
              >
                <Combobox.Control>
                  <Combobox.Input
                    placeholder={t('template.placeholder')}
                    asChild
                  >
                    <Input size="sm" />
                  </Combobox.Input>
                  <Combobox.Trigger asChild>
                    <IconButton
                      variant="link"
                      aria-label={tCommon('open')}
                      size="xs"
                      style={{
                        position: 'absolute',
                        right: '0.5rem',
                      }}
                    >
                      <ArrowUpDownIcon size={14} />
                    </IconButton>
                  </Combobox.Trigger>
                </Combobox.Control>
                <Combobox.Positioner>
                  <Combobox.Content>
                    <Combobox.ItemGroup>
                      {templates.map((template) => (
                        <Combobox.Item
                          key={template.id}
                          item={{
                            label: template.name,
                            value: template.id,
                          }}
                        >
                          <Combobox.ItemText>
                            {template.name}
                            {template.isDefault && (
                              <MetaDefault color="fg.muted" ml={1.5}>
                                ({t('template.defaultBadge')})
                              </MetaDefault>
                            )}
                          </Combobox.ItemText>
                          <Combobox.ItemIndicator>
                            <Tick01Icon size={14} />
                          </Combobox.ItemIndicator>
                        </Combobox.Item>
                      ))}
                    </Combobox.ItemGroup>
                  </Combobox.Content>
                </Combobox.Positioner>
              </Combobox.Root>
            )}
          </VStack>

          <VStack gap={2} w="full">
            {!isExecutiveSummary ? (
              <>
                <HStack
                  justifyContent="space-between"
                  w="full"
                  p={2}
                  bg="bg.subtle"
                  borderRadius="md"
                >
                  <HStack gap={1.5}>
                    <Image01Icon size={14} />
                    <BodyDefault>{t('options.includeScreenshots')}</BodyDefault>
                  </HStack>
                  <Switch
                    size="sm"
                    checked={includeScreenshots}
                    onCheckedChange={(details) =>
                      setIncludeScreenshots(details.checked)
                    }
                  />
                </HStack>

                <HStack
                  justifyContent="space-between"
                  w="full"
                  p={2}
                  bg="bg.subtle"
                  borderRadius="md"
                >
                  <HStack gap={1.5}>
                    <File01Icon size={14} />
                    <VStack gap={0} alignItems="flex-start">
                      <BodyDefault>
                        {t('options.includeRemediation')}
                      </BodyDefault>
                      <MetaDefault color="fg.muted">
                        {t('options.includeRemediationHint')}
                      </MetaDefault>
                    </VStack>
                  </HStack>
                  <Switch
                    size="sm"
                    checked={includeRemediation}
                    onCheckedChange={(details) =>
                      setIncludeRemediation(details.checked)
                    }
                  />
                </HStack>
              </>
            ) : (
              <HStack
                justifyContent="space-between"
                w="full"
                p={2}
                bg="bg.subtle"
                borderRadius="md"
              >
                <HStack gap={1.5}>
                  <File01Icon size={14} />
                  <VStack gap={0} alignItems="flex-start">
                    <BodyDefault>
                      {t('options.includeFindingsHighLevelOverview')}
                    </BodyDefault>
                    <MetaDefault color="fg.muted">
                      {t('options.includeFindingsHighLevelOverviewHint')}
                    </MetaDefault>
                  </VStack>
                </HStack>
                <Switch
                  size="sm"
                  checked={includeFindingsHighLevelOverview}
                  onCheckedChange={(details) =>
                    setIncludeFindingsHighLevelOverview(details.checked)
                  }
                />
              </HStack>
            )}

            <HStack
              justifyContent="space-between"
              w="full"
              p={2}
              bg="bg.subtle"
              borderRadius="md"
            >
              <HStack gap={1.5}>
                <SquareLock01Icon size={14} />
                <BodyDefault>{t('options.passwordProtect')}</BodyDefault>
              </HStack>
              <Switch
                size="sm"
                checked={passwordProtect}
                onCheckedChange={(details) =>
                  setPasswordProtect(details.checked)
                }
              />
            </HStack>

            {passwordProtect && (
              <VStack gap={1} alignItems="stretch" w="full">
                <MetaDefault color="fg.muted">
                  {t('options.customPasswordHint')}
                </MetaDefault>
                <Input
                  size="sm"
                  type="password"
                  placeholder={t('options.customPasswordPlaceholder')}
                  value={customPassword}
                  onChange={(event) => setCustomPassword(event.target.value)}
                />
              </VStack>
            )}
          </VStack>

          <Button
            size="sm"
            w="full"
            onClick={handleGenerate}
            disabled={!selectedTemplateId || isLoadingTemplates}
          >
            <File01Icon size={14} />
            {t('generatePdf')}
          </Button>
        </VStack>
      )}
    </VStack>
  );
}
